import ArrowLeftThin from '@assets/icons/arrow-left-thin.svg';
import i18n from '@/utils/i18n';
import NavigationDownCircleIcon from '@assets/icons/navigation-down-circle.svg';
import OrderIcon from '@assets/icons/orders.svg';
import React, { useCallback, useEffect, useMemo } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { AnalyticEvent } from '@/analytics/fennel/registry';
import { AnimatedSkeletonLoader } from '@/components/animated-skeleton-loader';
import { createStyles } from '@/contexts/theme-context';
import { displayInvoiceTotal } from '@/utils/invoice';
import { Divider } from '@usepepper/ui/atoms/divider';
import { EmptyState } from '@/components/empty-state';
import { fennelTrack } from '@/analytics/fennel/hook';
import {
  FlatList,
  SafeAreaView,
  ScrollView,
  TouchableOpacity,
  View,
} from 'react-native';
import { formatCurrency, formatPercent, formatUnitCount } from '@/utils/money';
import { formatDate, momentWithTimezone } from '@/utils/date';
import { GET_INVOICE_DETAIL_QUERY } from '@/screens/invoice-detail/invoice-detail-query';
import { INSIGHTS_HUB_ROUTES } from '@/navigators/routes';
import { InsightsHubStackParams } from '@/navigators/types';
import {
  Invoice_GetInvoiceByIdQuery,
  Invoice_GetInvoiceByIdQueryVariables,
  Order_Invoice_Line_Items,
} from '@/types';
import { InvoiceLineItem } from '@/components/orders/invoice-line-item';
import { ListEmptyState } from '@/components/list-empty-state';
import { PageContainer } from '@/components/page-container';
import { SearchInput } from '@/components/search-input';
import { StackNavigationProp, StackScreenProps } from '@react-navigation/stack';
import { Typography } from '@usepepper/ui/atoms/typography';
import {
  useGetDynamicConfigValueForChat,
  useSupplierDynamicConfigFlag,
} from '@/hooks/use-dynamic-config-value';
import { useGetOrderInvoiceGrossProfit } from '@/components/orders/hooks/use-get-invoice';
import { useHubCardNavigation } from '@/hooks/insights-hub/use-hub-card-navigation';
import { useMultiSearchFilters } from '@/hooks/use-multi-search-filters';
import { useNavigation } from '@react-navigation/native';
import { useSupplierCurrencyCode } from '@/hooks/use-supplier-currency-code';
import { useUserContext } from '@/contexts/user-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

type Props = StackScreenProps<
  InsightsHubStackParams,
  INSIGHTS_HUB_ROUTES.InvoiceDetails
>;

type NavigationType = StackNavigationProp<
  InsightsHubStackParams,
  INSIGHTS_HUB_ROUTES.InvoiceDetails
>;

const getSearchableFields = (item: Order_Invoice_Line_Items) => {
  const invoiceItem = item;
  return [
    invoiceItem.item_name ?? '',
    invoiceItem.item_code ?? '',
    invoiceItem.variant_pack?.external_item_id ?? '',
  ];
};

export const InvoiceDetailV2Screen: React.FC<Props> = ({ route }) => {
  const { goBack } = useNavigation<NavigationType>();
  const { navigateToItemDetails } = useHubCardNavigation();

  const [styles, theme] = useStyles();
  const { getDynamicConfigValueForChat } = useGetDynamicConfigValueForChat();
  const { restaurantUUIDToRelationshipMap } = useUserContext();

  const { restaurantUUID, invoiceUUID, supplierUUID } = route.params;
  const { currencyCode: supplierCurrencyCode } =
    useSupplierCurrencyCode(supplierUUID);
  const { data, loading } = useWrappedQuery<
    Invoice_GetInvoiceByIdQuery,
    Invoice_GetInvoiceByIdQueryVariables
  >(GET_INVOICE_DETAIL_QUERY, {
    variables: {
      invoiceUUID,
    },
  });
  const { grossProfit, loading: grossProfitLoading } =
    useGetOrderInvoiceGrossProfit(invoiceUUID);

  const restaurant = restaurantUUIDToRelationshipMap[restaurantUUID];
  const invoice = data?.getOrderInvoiceById.order_invoice;
  const chatUUID = invoice?.chat_uuid;
  const consumptionTax = chatUUID
    ? getDynamicConfigValueForChat(chatUUID, 'consumption_tax')
    : null;
  const showGrossProfit = !useSupplierDynamicConfigFlag(
    'sales_rep_config',
    'disable_gross_profit_display',
  );

  // graphQL type Order_Invoice_Line_Items requires an `id` field due to Node interface,
  // but API response only provides `uuid`. We map `uuid` to `id` here to satisfy the type contract
  const { filteredData, onDisplayTextChange, searchText, searching } =
    useMultiSearchFilters<Order_Invoice_Line_Items>({
      data: (invoice?.order_invoice_line_items ?? []).map((item) => ({
        ...item,
        id: item.uuid,
      })) as Order_Invoice_Line_Items[],
      getSearchableFields,
    });

  useEffect(() => {
    fennelTrack(AnalyticEvent.INVOICE_DETAIL_VIEW, {
      invoiceUUID,
      restaurantUUID,
      screenName: INSIGHTS_HUB_ROUTES.InvoiceDetails,
      supplierUUID,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const invoiceDate = useMemo(() => {
    if (!invoice) {
      return null;
    }

    return invoice.invoice_date
      ? formatDate(momentWithTimezone(invoice.invoice_date))
      : null;
  }, [invoice]);

  // usually only shown if invoice is payable, but we show it for all invoices here since this is a DSR-view
  const invoiceDueAt = useMemo(() => {
    if (!invoice) {
      return null;
    }

    return invoice.due_at
      ? i18n.t('payment_pay_by_date', {
          date: formatDate(momentWithTimezone(invoice.due_at)),
        })
      : null;
  }, [invoice]);

  const noSearchResults =
    searchText.length > 0 ? (
      <ListEmptyState
        text={i18n.t('no_results_for', {
          searchTerm: searchText,
        })}
      />
    ) : null;

  const invoiceUnitCount =
    invoice?.order_invoice_line_items.reduce(
      (result, oili) => result + oili.ship_quantity,
      0,
    ) ?? 0;

  const totalBalance = displayInvoiceTotal(
    invoice?.order_invoice_line_items || [],
    supplierCurrencyCode,
    consumptionTax,
  );

  const onItemPress = useCallback(
    (item: Order_Invoice_Line_Items) => {
      if (!item.variant_pack_uuid) {
        return;
      }
      navigateToItemDetails(
        restaurantUUID,
        supplierUUID,
        item.variant_pack_uuid,
        INSIGHTS_HUB_ROUTES.InvoiceDetails,
      );
      fennelTrack(AnalyticEvent.ORDER_ITEM_PRESS, {
        isDraft: false,
        metadata: { dataSource: 'invoice' },
        orderItemUUID: item.uuid,
        orderUUID: invoiceUUID,
        origin: INSIGHTS_HUB_ROUTES.InvoiceDetails,
        restaurantUUID,
        supplierUUID,
        variantPackUUID: item.variant_pack_uuid,
      });
    },
    [navigateToItemDetails, restaurantUUID, supplierUUID, invoiceUUID],
  );

  return (
    <PageContainer
      containerStyle={styles.container}
      navigationHeaderProps={{
        BackButtonIcon: () => (
          <TouchableOpacity accessibilityRole="button" onPress={goBack}>
            <ArrowLeftThin fill={theme.sk.color.icon.primary} />
          </TouchableOpacity>
        ),
        showBackButton: true,
        showShadow: false,
      }}
    >
      <SafeAreaView style={styles.invoiceDetailsContainer}>
        {loading ? (
          <View
            accessibilityState={{ busy: true }}
            style={styles.loadingContainer}
          >
            <ActivityIndicator />
          </View>
        ) : !invoice ? (
          // TODO(DSR-3135): update empty state for invoices
          <EmptyState
            description={i18n.t('order_detail.empty_state.description')}
            icon={<OrderIcon stroke={theme.sk.color.icon.primary} />}
            title={i18n.t('order_detail.empty_state.title')}
          />
        ) : (
          <>
            <ScrollView showsVerticalScrollIndicator={false}>
              <View style={styles.invoiceDetailsScrollableContent}>
                <View style={styles.invoiceHeaderContainer}>
                  <View>
                    <Typography
                      color={theme.sk.color.text.primary}
                      variant="paragraph-base"
                    >
                      {restaurant.name}
                    </Typography>
                    <Typography variant="heading-xl">{invoiceDate}</Typography>
                  </View>
                  <View style={styles.detailsRow}>
                    <OrderIcon stroke={theme.sk.color.icon.primary} />
                    <Typography variant="paragraph-md">
                      {i18n.t('invoice_number', {
                        number: invoice.invoice_number,
                      })}
                    </Typography>
                  </View>
                  {invoice.due_at ? (
                    <View style={styles.detailsRow}>
                      <NavigationDownCircleIcon
                        stroke={theme.sk.color.icon.primary}
                      />
                      <Typography variant="paragraph-md">
                        {invoiceDueAt}
                      </Typography>
                    </View>
                  ) : null}
                </View>
                <SearchInput
                  autoCorrect={false}
                  containerStyle={styles.searchContainer}
                  isLoading={searching}
                  onChangeText={onDisplayTextChange}
                  placeholder={i18n.t('find_in_this_order')}
                  value={searchText}
                />
                <View collapsable={false}>
                  <FlatList
                    data={filteredData}
                    ItemSeparatorComponent={() => (
                      <Divider style={styles.divider} />
                    )}
                    keyExtractor={(item) => item.uuid}
                    ListEmptyComponent={noSearchResults}
                    renderItem={({ item }) => (
                      <TouchableOpacity
                        disabled={!item.variant_pack_uuid}
                        onPress={() => onItemPress(item)}
                      >
                        <InvoiceLineItem
                          invoiceCurrencyCode={supplierCurrencyCode}
                          item={item}
                        />
                      </TouchableOpacity>
                    )}
                    scrollEnabled={false}
                    showsVerticalScrollIndicator={false}
                  />
                  <Divider style={styles.lastDivider} />
                </View>
              </View>
            </ScrollView>
            <View style={styles.invoiceSummaryContainer}>
              {showGrossProfit && grossProfitLoading ? (
                <View style={styles.invoiceSummaryLine}>
                  <Typography
                    color={theme.sk.color.text.tertiary}
                    variant="paragraph-base"
                  >
                    {`${i18n.t('gross_profit')}`}
                  </Typography>
                  <AnimatedSkeletonLoader
                    height={16}
                    style={styles.grossProfitSkeleton}
                    width="25%"
                  />
                </View>
              ) : showGrossProfit && grossProfit ? (
                <View style={styles.invoiceSummaryLine}>
                  <Typography
                    color={theme.sk.color.text.tertiary}
                    variant="paragraph-base"
                  >
                    {i18n.t('gross_profit')}
                  </Typography>
                  <Typography
                    color={theme.sk.color.text.secondary}
                    variant="paragraph-base"
                  >
                    {[
                      formatPercent(grossProfit.percentage),
                      formatCurrency(
                        grossProfit.amount_in_micros,
                        grossProfit.currency_code,
                      ),
                    ].join(' • ')}
                  </Typography>
                </View>
              ) : null}
              <View style={styles.invoiceSummaryLine}>
                <Typography
                  color={theme.sk.color.text.primary}
                  variant="paragraph-base"
                >
                  {`${formatUnitCount(invoiceUnitCount)} ${invoiceUnitCount === 1 ? i18n.t('pack_unit_unit') : i18n.t('units')}`}
                </Typography>
                <Typography variant="heading-md">{totalBalance}</Typography>
              </View>
            </View>
          </>
        )}
      </SafeAreaView>
    </PageContainer>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    backgroundColor: theme.sk.color.bg.primary.default,
  },
  detailsRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  divider: {
    marginVertical: 12,
  },
  grossProfitSkeleton: {
    alignSelf: 'center',
  },
  invoiceDetailsContainer: {
    flex: 1,
    gap: 16,
    justifyContent: 'space-between',
    marginHorizontal: 16,
  },
  invoiceDetailsScrollableContent: {
    gap: 16,
  },
  invoiceHeaderContainer: {
    gap: 4,
  },
  invoiceSummaryContainer: {
    gap: 8,
    marginBottom: theme.sk.spacing.spaceLarge,
  },
  invoiceSummaryLine: {
    alignItems: 'flex-end',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  lastDivider: {
    marginTop: 12,
  },
  loadingContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  searchContainer: {
    backgroundColor: theme.sk.color.bg.tertiary.default,
    height: 48,
    marginBottom: 8,
  },
}));
